import React, { useState } from 'react'
import { Layout, Menu, Button, Space, MenuProps, Dropdown } from 'antd'
import { MenuOutlined, CloseOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useModalStore } from '../../store/ModalStore'
import { MODAL_STATE } from '../../types/modals'
import './Header.css'
import { useSmartNavigate } from '../../hooks/useSmartNavigate'

const { Header } = Layout

const HeaderModule: React.FC = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const { openModal } = useModalStore()
    const smartNavigate = useSmartNavigate()
    const { t, i18n } = useTranslation()

    const menuItems: MenuProps['items'] = [
        { key: '/', label: t('header.home') },
        // Not part of first release
        // { key: 'tournaments', label: t('header.tournaments') },
        // { key: 'for companies', label: t('header.forCompanies') },
        { key: '/about', label: t('header.aboutUs') },
    ]

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        smartNavigate(e.key)
        // Close mobile menu if open
        setIsMobileMenuOpen(false)
    }

    const handleLanguageChange = (language: string) => {
        i18n.changeLanguage(language)
        localStorage.setItem('i18nextLng', language)
        setIsMobileMenuOpen(false)
    }

    const languageMenuItems: MenuProps['items'] = [
        {
            key: 'en-US',
            label: 'EN',
            onClick: () => handleLanguageChange('en-US'),
        },
        {
            key: 'de-DE',
            label: 'DE',
            onClick: () => handleLanguageChange('de-DE'),
        },
    ]

    const currentLanguage = i18n.language === 'de-DE' ? 'DE' : 'EN'

    return (
        <Header className="header">
            {/* Mobile Menu Button */}
            <Button
                className="mobile-menu-button"
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setIsMobileMenuOpen(true)}
            />

            <div className="header-content">
                <img
                    src="/img/DailyVents_logo.svg"
                    alt={t('common.alt.dailyVentsLogo')}
                    onClick={() => smartNavigate('/')}
                    style={{ cursor: 'pointer' }}
                />
                <Menu
                    mode="horizontal"
                    selectedKeys={[window.location.pathname]}
                    items={menuItems}
                    onClick={handleMenuClick}
                />
            </div>

            <Space size={24}>
                <Dropdown
                    menu={{ items: languageMenuItems }}
                    trigger={['click']}
                    placement="bottomRight"
                    className="language-switcher-dropdown"
                >
                    <Button type="text" className="language-switcher">
                        {currentLanguage}
                    </Button>
                </Dropdown>
                <Button
                    type="primary"
                    onClick={() => openModal(MODAL_STATE.TRY_IT)}
                    className="try-it-out-btn"
                >
                    {t('header.tryItOut')}
                </Button>
            </Space>

            {/* Mobile Side Panel */}
            <div
                className={`mobile-side-panel ${isMobileMenuOpen ? 'open' : ''}`}
            >
                <div className="mobile-side-panel-content">
                    <div className="mobile-side-panel-header">
                        <img
                            src="/img/DailyVents_logo.svg"
                            alt={t('common.alt.dailyVentsLogo')}
                            onClick={() => {
                                smartNavigate('/')
                                setIsMobileMenuOpen(false)
                            }}
                            style={{ cursor: 'pointer' }}
                        />
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="mobile-menu-close"
                        />
                    </div>
                    <div className="mobile-side-panel-menu">
                        <Menu
                            mode="vertical"
                            defaultSelectedKeys={['home']}
                            items={menuItems}
                            className="mobile-menu"
                            onClick={handleMenuClick}
                        />
                        <div className="mobile-language-switcher-container">
                            <Dropdown
                                menu={{ items: languageMenuItems }}
                                trigger={['click']}
                                placement="bottomLeft"
                                className="mobile-language-switcher-dropdown"
                            >
                                <Button
                                    type="text"
                                    className="mobile-language-switcher"
                                >
                                    {currentLanguage}
                                </Button>
                            </Dropdown>
                        </div>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => {
                                openModal(MODAL_STATE.TRY_IT)
                                setIsMobileMenuOpen(false)
                            }}
                            className="mobile-try-button"
                        >
                            {t('header.tryItOut')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="mobile-overlay"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}
        </Header>
    )
}

export default HeaderModule
